import {
  DYNAMIC_ASSET_TYPE,
  DYNAMIC_KEY_TO_TYPE,
} from 'components/VideoTemplateEditor/integrations/useDynamicPodcastTextIntegration';
import { KeyTextType } from 'types';
import {
  DEFAULT_EPISODE_TITLE,
  DEFAULT_PODCAST_TITLE,
  DYNAMIC_ELEMENTS,
  KEY_TEXT_TYPE,
} from 'utils/constants';
import { textContentFromHtmlString } from 'utils/dom';
import { DEFAULT_TEXT } from '../utils';

/**
 * Determines if a text asset is dynamic by checking both the HTML content and text type.
 *
 * It's tricky to check if an asset is dynamic or not because it can be created in many
 * different ways:
 * - By enabling them in the advanced editor, which then sets the textType prop that
 *   guarantees that the asset is dynamic.
 * - By manually typing the dynamic key in the text input (e.g., '%%Episode%%' or
 *   '%%Podcast%%').
 * - For templates created from the UCS, only the dynamic asset key is available and
 *   the textType is not set.
 *
 * Because of all these scenarios, this function checks both:
 * 1. The actual text content for dynamic placeholders (to catch manually typed keys).
 * 2. The textType property (if defined, it's a reliable indicator).
 *
 * @param textHtml - The HTML content of the text overlay.
 * @param textType - Optional text type that may indicate a dynamic asset.
 *
 * @returns true if the asset is dynamic, false otherwise.
 */
export const checkIsDynamicAsset = (
  textHtml: string,
  textType?: KeyTextType,
): boolean => {
  return (
    DYNAMIC_ELEMENTS.includes(textContentFromHtmlString(textHtml)) ||
    KEY_TEXT_TYPE.includes(textType)
  );
};

/**
 * Retrieves the default text value from the text content, handling dynamic placeholders.
 *
 * This function normalizes text content by replacing dynamic placeholders or empty
 * content with a default text value. This is useful when reverting dynamic content
 * back to a static state or initializing empty text fields.
 *
 * The function handles several scenarios:
 * - Empty or undefined text content → returns DEFAULT_TEXT.
 * - Dynamic placeholders (e.g., '%%Episode%%', '%%Podcast%%') → returns DEFAULT_TEXT.
 * - Regular text content → returns the text content as-is.
 *
 * @param textContent - The text content to process (can be HTML string).
 *
 * @returns The default text value if content is empty or dynamic, otherwise the
 * original text content.
 */
export const getDefaultTextValue = (textContent: string): string => {
  const isDynamic = checkIsDynamicAsset(textContent);

  if (!textContent || isDynamic) {
    return DEFAULT_TEXT;
  }

  return textContent;
};

/**
 * Determines if the new text type selection matches the currently active text type.
 *
 * This is used to implement toggle behavior: if the user selects the same dynamic
 * text type that's already active, it should be disabled (reverted to initial value).
 *
 * It's tricky to check if an asset is dynamic or not because it can be created in many
 * different ways:
 * - By enabling them in the advanced editor, which then sets the textType prop that
 *   guarantees that the asset is dynamic.
 * - By manually typing the dynamic key in the text input (e.g., '%%Episode%%' or
 *   '%%Podcast%%').
 * - For templates created from the UCS, only the dynamic asset key is available and
 *   the textType is not set.
 *
 * Because of all these scenarios, this function handles two cases:
 * 1. When currentTextType is defined: Direct comparison with the newTextType.
 *    This is the straightforward case where the textType was explicitly set.
 * 2. When currentTextType is undefined: The text may still contain a dynamic placeholder
 *    in the textValue. In this case, we derive the corresponding KeyTextType by:
 *    - Mapping the placeholder (e.g., '%%Episode%%') to its DynamicTitleType ('episode').
 *    - Mapping the DynamicTitleType to its KeyTextType ('episodeTitle').
 *    - Comparing the derived KeyTextType with the newTextType.
 *
 * @param currentTextType - The current text type (may be undefined if not explicitly set).
 * @param newTextType - The text type being selected (e.g., 'episodeTitle', 'podcastTitle').
 * @param textValue - The current text value (may contain dynamic placeholders like '%%Episode%%').
 * @param isDynamicContentEnabled - Whether dynamic content is currently enabled.
 *
 * @returns true if the new type matches the current active type, false otherwise.
 */
export const checkIsSameValue = (
  currentTextType: KeyTextType | undefined,
  newTextType: KeyTextType,
  textValue: string,
  isDynamicContentEnabled: boolean,
): boolean => {
  // Case 1: currentTextType is explicitly defined.
  if (currentTextType !== undefined) {
    // Direct comparison: if they match, user is clicking the same option again.
    return currentTextType === newTextType;
  }

  // Case 2: currentTextType is undefined, but text may still contain a dynamic
  // placeholder. This handles manually typed keys and UCS-created templates.
  // If dynamic content is disabled, there's no active dynamic type to compare.
  if (!isDynamicContentEnabled) {
    return false;
  }

  // Extract the dynamic placeholder type from the text value.
  // Example: textValue = '%%Episode%%' -> DYNAMIC_KEY_TO_TYPE['%%Episode%%'] = 'episode'
  const dynamicTitleType = DYNAMIC_KEY_TO_TYPE[textValue];

  // If no matching dynamic type found in the text value, they're not the same.
  if (!dynamicTitleType) {
    return false;
  }

  // Convert DynamicTitleType to KeyTextType for comparison.
  // Example: 'episode' -> DYNAMIC_ASSET_TYPE['episode'] = 'episodeTitle'
  const currentDynamicTextType = DYNAMIC_ASSET_TYPE[dynamicTitleType];

  // Compare the derived type with the newly selected type.
  return currentDynamicTextType === newTextType;
};

/**
 * Returns the dynamic placeholder string for a given text type.
 *
 * Maps a KeyTextType to its corresponding dynamic placeholder value that will be
 * inserted into the text content when dynamic content is enabled. This is used
 * when applying dynamic content to text overlays.
 *
 * @param textType - The text type to get the placeholder for (e.g., 'episodeTitle', 'podcastTitle').
 *
 * @returns The default text value for the given text type.
 */
export const getDynamicPlaceholderForTextType = (
  textType: KeyTextType,
): string => {
  return textType === 'episodeTitle'
    ? DEFAULT_EPISODE_TITLE
    : DEFAULT_PODCAST_TITLE;
};
